import { ApolloError } from "@apollo/client";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  GridItem,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { useForm } from "react-hook-form";

import { Alert } from "../../../components";
import { UserRoleName } from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
  getOrganizationNotificationSetting,
  OrganizationNotificationChannel,
  OrganizationNotificationSettingsMap,
  OrganizationNotificationType,
} from "./notificationSettingsUtils";

export interface FormData {
  interviewRecapSubscriptionEmail: boolean;
  interviewRecapSubscriptionSlack: boolean;
  interviewRecapSubscriptionTeams: boolean;
  interviewPrepSubscriptionEmail: boolean;
  interviewPrepSubscriptionSlack: boolean;
  interviewPrepSubscriptionTeams: boolean;
  weeklyRecapSubscriptionEmail: boolean;
  weeklyRecapSubscriptionSlack: boolean;
  weeklyRecapSubscriptionTeams: boolean;
  monthlyAnalyticsSubscriptionEmail: boolean;
  monthlyAnalyticsSubscriptionTeams: boolean;
  callShareSubscriptionEmail: boolean;
  callShareSubscriptionSlack: boolean;
  callShareSubscriptionTeams: boolean;
  callNoteSubscriptionEmail: boolean;
  callNoteSubscriptionSlack: boolean;
  callNoteSubscriptionTeams: boolean;
  morningDigestSubscriptionEmail: boolean;
  morningDigestSubscriptionSlack: boolean;
  morningDigestSubscriptionTeams: boolean;
  coachingSubscriptionEmail: boolean;
  coachingSubscriptionSlack: boolean;
  coachingSubscriptionTeams: boolean;
  candidateRediscoverySubscriptionEmail: boolean;
  candidateRediscoverySubscriptionTeams: boolean;
}

interface NotificationsSettingsFormProps {
  interviewRecapSubscriptionEmail: boolean;
  interviewRecapSubscriptionSlack: boolean;
  interviewRecapSubscriptionTeams: boolean;
  interviewPrepSubscriptionEmail: boolean;
  interviewPrepSubscriptionSlack: boolean;
  interviewPrepSubscriptionTeams: boolean;
  weeklyRecapSubscriptionEmail: boolean;
  weeklyRecapSubscriptionSlack: boolean;
  weeklyRecapSubscriptionTeams: boolean;
  monthlyAnalyticsSubscriptionEmail: boolean;
  monthlyAnalyticsSubscriptionTeams: boolean;
  callShareSubscriptionEmail: boolean;
  callShareSubscriptionSlack: boolean;
  callShareSubscriptionTeams: boolean;
  callNoteSubscriptionEmail: boolean;
  callNoteSubscriptionSlack: boolean;
  callNoteSubscriptionTeams: boolean;
  morningDigestSubscriptionEmail: boolean;
  morningDigestSubscriptionSlack: boolean;
  morningDigestSubscriptionTeams: boolean;
  coachingSubscriptionEmail: boolean;
  coachingSubscriptionSlack: boolean;
  coachingSubscriptionTeams: boolean;
  candidateRediscoverySubscriptionEmail: boolean;
  candidateRediscoverySubscriptionTeams: boolean;
  organizationNotificationSettings: OrganizationNotificationSettingsMap;
  error: ApolloError | undefined;
  onSubmit: (formData: FormData) => void;
}

const NotificationsSettingsForm: React.FC<NotificationsSettingsFormProps> = ({
  interviewRecapSubscriptionEmail,
  interviewRecapSubscriptionSlack,
  interviewRecapSubscriptionTeams,
  interviewPrepSubscriptionEmail,
  interviewPrepSubscriptionSlack,
  interviewPrepSubscriptionTeams,
  weeklyRecapSubscriptionEmail,
  weeklyRecapSubscriptionSlack,
  weeklyRecapSubscriptionTeams,
  monthlyAnalyticsSubscriptionEmail,
  monthlyAnalyticsSubscriptionTeams,
  callShareSubscriptionEmail,
  callShareSubscriptionSlack,
  callShareSubscriptionTeams,
  callNoteSubscriptionEmail,
  callNoteSubscriptionSlack,
  callNoteSubscriptionTeams,
  morningDigestSubscriptionEmail,
  morningDigestSubscriptionSlack,
  morningDigestSubscriptionTeams,
  coachingSubscriptionEmail,
  coachingSubscriptionSlack,
  coachingSubscriptionTeams,
  candidateRediscoverySubscriptionEmail,
  candidateRediscoverySubscriptionTeams,
  organizationNotificationSettings,
  error,
  onSubmit,
}) => {
  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: {
      interviewRecapSubscriptionEmail,
      interviewRecapSubscriptionSlack,
      interviewRecapSubscriptionTeams,
      interviewPrepSubscriptionEmail,
      interviewPrepSubscriptionSlack,
      interviewPrepSubscriptionTeams,
      weeklyRecapSubscriptionEmail,
      weeklyRecapSubscriptionSlack,
      weeklyRecapSubscriptionTeams,
      monthlyAnalyticsSubscriptionEmail,
      monthlyAnalyticsSubscriptionTeams,
      callShareSubscriptionEmail,
      callShareSubscriptionSlack,
      callShareSubscriptionTeams,
      callNoteSubscriptionEmail,
      callNoteSubscriptionSlack,
      callNoteSubscriptionTeams,
      morningDigestSubscriptionEmail,
      morningDigestSubscriptionSlack,
      morningDigestSubscriptionTeams,
      coachingSubscriptionEmail,
      coachingSubscriptionSlack,
      coachingSubscriptionTeams,
      candidateRediscoverySubscriptionEmail,
      candidateRediscoverySubscriptionTeams,
    },
  });

  const currentUser = useCurrentUser();
  const handleCheckboxChange = (): void => {
    handleSubmit((formData) =>
      onSubmit({
        ...formData,
      })
    )();
  };

  const renderCheckbox = (
    fieldName: keyof FormData,
    defaultChecked: boolean,
    notificationType: OrganizationNotificationType,
    channel: OrganizationNotificationChannel,
    checkboxProps?: { isDisabled?: boolean }
  ): JSX.Element => {
    const orgSetting = getOrganizationNotificationSetting(
      organizationNotificationSettings,
      notificationType,
      channel
    );
    if (orgSetting === "USER_CHOICE") {
      return (
        <Checkbox
          {...register(fieldName, {
            onChange: handleCheckboxChange,
          })}
          defaultChecked={defaultChecked}
          isDisabled={checkboxProps?.isDisabled}
        />
      );
    }

    return (
      <>
        <Tooltip
          label="You can't edit this notification because it is controlled by your organization."
          shouldWrapChildren
        >
          <Box display="inline-flex" alignItems="center">
            <Checkbox
              isChecked={orgSetting === "FORCE_ENABLE"}
              {...checkboxProps}
              isDisabled
              pointerEvents="none"
            />
          </Box>
        </Tooltip>
        <input
          type="hidden"
          {...register(fieldName, {
            setValueAs: (value) => value === "true",
          })}
          defaultValue={String(defaultChecked)}
        />
      </>
    );
  };

  const { slackAppInstalled } = currentUser.organization;
  const { isTeamsAppInstalled } = currentUser;
  const isTeamsNotificationsEnabled =
    isTeamsAppInstalled &&
    currentUser.organization.teamsAppSettings?.allNotificationsEnabled;
  const coachingEnabled = useFeatureFlag("coaching:v1");
  const rediscoveryEnabled = useFeatureFlag("rediscovery:v1");

  return (
    <form>
      {error?.graphQLErrors?.map(({ message }, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Alert mb={6} key={i} status="error" description={message} />
      ))}
      <Grid
        h="auto"
        templateColumns="repeat(15, 1fr)"
        gridAutoRows="min-content"
        columnGap={14}
      >
        {/* Header */}
        <GridItem colSpan={5}>
          <Text color="gray.500" fontSize="sm" fontWeight="bold">
            Notify me when...
          </Text>
        </GridItem>
        <GridItem colSpan={2}>
          <Text color="gray.500" fontSize="sm" fontWeight="bold">
            via Email
          </Text>
        </GridItem>
        <GridItem colSpan={2}>
          <Text
            color="gray.500"
            fontSize="sm"
            fontWeight="bold"
            hidden={!slackAppInstalled}
          >
            via Slack
          </Text>
        </GridItem>
        <GridItem colSpan={2}>
          <Text
            color="gray.500"
            fontSize="sm"
            fontWeight="bold"
            hidden={!isTeamsNotificationsEnabled}
          >
            via Teams
          </Text>
        </GridItem>
        {/* interview recording ready */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            My interview recording is ready
          </Text>
          <Text fontSize="xs">
            Includes any timestamped notes, reactions, interview questions, and
            a link to view your full recording and transcript.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl id="interviewRecapSubscriptionEmail" top="25%">
            {renderCheckbox(
              "interviewRecapSubscriptionEmail",
              interviewRecapSubscriptionEmail,
              "INTERVIEW_RECAP",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="interviewRecapSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderCheckbox(
              "interviewRecapSubscriptionSlack",
              interviewRecapSubscriptionSlack,
              "INTERVIEW_RECAP",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="interviewRecapSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderCheckbox(
              "interviewRecapSubscriptionTeams",
              interviewRecapSubscriptionTeams,
              "INTERVIEW_RECAP",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* upcoming interviews */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            I have upcoming interviews
          </Text>
          <Text fontSize="xs">
            Includes links to previous recordings for your candidate, and
            opt-out links for your upcoming interview recording.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl id="interviewPrepSubscriptionEmail" top="25%">
            {renderCheckbox(
              "interviewPrepSubscriptionEmail",
              interviewPrepSubscriptionEmail,
              "INTERVIEW_PREP",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="interviewPrepSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderCheckbox(
              "interviewPrepSubscriptionSlack",
              interviewPrepSubscriptionSlack,
              "INTERVIEW_PREP",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="interviewPrepSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderCheckbox(
              "interviewPrepSubscriptionTeams",
              interviewPrepSubscriptionTeams,
              "INTERVIEW_PREP",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* weekly recap */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            My weekly recap is ready
          </Text>
          <Text fontSize="xs">
            Includes a summary of your total interview time that week, with
            links to all your recordings.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl id="weeklyRecapSubscriptionEmail" top="25%">
            {renderCheckbox(
              "weeklyRecapSubscriptionEmail",
              weeklyRecapSubscriptionEmail,
              "WEEKLY_RECAP",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="weeklyRecapSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderCheckbox(
              "weeklyRecapSubscriptionSlack",
              weeklyRecapSubscriptionSlack,
              "WEEKLY_RECAP",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="weeklyRecapSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderCheckbox(
              "weeklyRecapSubscriptionTeams",
              weeklyRecapSubscriptionTeams,
              "WEEKLY_RECAP",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* monthly analytics recap */}
        {currentUser.userRole?.name === UserRoleName.SiteAdmin && (
          <>
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                My monthly insights recap is ready
              </Text>
              <Text fontSize="xs">
                Includes a summary of your total interviews for the month, with
                links to featured metrics.
              </Text>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl id="monthlyAnalyticsSubscriptionEmail" top="25%">
                {renderCheckbox(
                  "monthlyAnalyticsSubscriptionEmail",
                  monthlyAnalyticsSubscriptionEmail,
                  "MONTHLY_ANALYTICS",
                  "EMAIL"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl
                id="monthlyAnalyticsSubscriptionSlack"
                top="25%"
                hidden
              >
                <Checkbox disabled />
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl
                id="monthlyAnalyticsSubscriptionTeams"
                top="25%"
                hidden
              >
                {renderCheckbox(
                  "monthlyAnalyticsSubscriptionTeams",
                  monthlyAnalyticsSubscriptionTeams,
                  "MONTHLY_ANALYTICS",
                  "TEAMS",
                  { isDisabled: true }
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
          </>
        )}

        {/* morning digest */}
        {(slackAppInstalled || isTeamsNotificationsEnabled) && (
          <>
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                My morning digest is ready
              </Text>
              <Text fontSize="xs">
                Includes a list of your scheduled interviews for that day.
              </Text>
            </GridItem>
            {/* We don't currently have a morning digest email notification, but will potentially add one down the line. */}
            <GridItem mt="20px" colSpan={2}>
              <FormControl id="morningDigestSubscriptionEmail" top="25%" hidden>
                {renderCheckbox(
                  "morningDigestSubscriptionEmail",
                  morningDigestSubscriptionEmail,
                  "MORNING_DIGEST",
                  "EMAIL",
                  { isDisabled: true }
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl
                id="morningDigestSubscriptionSlack"
                top="25%"
                hidden={!slackAppInstalled}
              >
                {renderCheckbox(
                  "morningDigestSubscriptionSlack",
                  morningDigestSubscriptionSlack,
                  "MORNING_DIGEST",
                  "SLACK"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl
                id="morningDigestSubscriptionTeams"
                top="25%"
                hidden={!isTeamsNotificationsEnabled}
              >
                {renderCheckbox(
                  "morningDigestSubscriptionTeams",
                  morningDigestSubscriptionTeams,
                  "MORNING_DIGEST",
                  "TEAMS"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
          </>
        )}
        {/* call share */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Someone shares an interview, clip or playlist with me
          </Text>
          <Text fontSize="xs">
            Includes a link to the interview, clip or playlist that was shared.
          </Text>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl id="callShareSubscriptionEmail" top="25%">
            {renderCheckbox(
              "callShareSubscriptionEmail",
              callShareSubscriptionEmail,
              "CALL_SHARE",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="callShareSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderCheckbox(
              "callShareSubscriptionSlack",
              callShareSubscriptionSlack,
              "CALL_SHARE",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="callShareSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderCheckbox(
              "callShareSubscriptionTeams",
              callShareSubscriptionTeams,
              "CALL_SHARE",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="22px" colSpan={15}>
          <Divider borderColor="gray.200" />
        </GridItem>
        {/* call note */}
        <GridItem mt="20px" colSpan={5}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Someone leaves a comment on my interview or clip
          </Text>
          <Text fontSize="xs">Includes a link to the comment.</Text>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl id="callNoteSubscriptionEmail" top="25%">
            {renderCheckbox(
              "callNoteSubscriptionEmail",
              callNoteSubscriptionEmail,
              "CALL_NOTE",
              "EMAIL"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="callNoteSubscriptionSlack"
            top="25%"
            hidden={!slackAppInstalled}
          >
            {renderCheckbox(
              "callNoteSubscriptionSlack",
              callNoteSubscriptionSlack,
              "CALL_NOTE",
              "SLACK"
            )}
          </FormControl>
        </GridItem>
        <GridItem mt="20px" colSpan={2}>
          <FormControl
            id="callNoteSubscriptionTeams"
            top="25%"
            hidden={!isTeamsNotificationsEnabled}
          >
            {renderCheckbox(
              "callNoteSubscriptionTeams",
              callNoteSubscriptionTeams,
              "CALL_NOTE",
              "TEAMS"
            )}
          </FormControl>
        </GridItem>
        {coachingEnabled && (
          <>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
            {/* interviewing tips */}
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                Interviewing tips
              </Text>
              <Text fontSize="xs">
                Includes customized interviewing tips based on your recent
                interviews.
              </Text>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl id="coachingSubscriptionEmail" top="25%">
                {renderCheckbox(
                  "coachingSubscriptionEmail",
                  coachingSubscriptionEmail,
                  "COACHING",
                  "EMAIL"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl
                id="coachingSubscriptionSlack"
                top="25%"
                hidden={!slackAppInstalled}
              >
                {renderCheckbox(
                  "coachingSubscriptionSlack",
                  coachingSubscriptionSlack,
                  "COACHING",
                  "SLACK"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl id="coachingSubscriptionTeams" top="25%" hidden>
                {renderCheckbox(
                  "coachingSubscriptionTeams",
                  coachingSubscriptionTeams,
                  "COACHING",
                  "TEAMS",
                  { isDisabled: true }
                )}
              </FormControl>
            </GridItem>
          </>
        )}
        {rediscoveryEnabled && (
          <>
            <GridItem mt="22px" colSpan={15}>
              <Divider borderColor="gray.200" />
            </GridItem>
            {/* rediscovery */}
            <GridItem mt="20px" colSpan={5}>
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                BrightHire identifies potential candidates for my role
              </Text>
              <Text fontSize="xs">
                Includes a link to a list of potential candidates.
              </Text>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl id="candidateRediscoverySubscriptionEmail" top="25%">
                {renderCheckbox(
                  "candidateRediscoverySubscriptionEmail",
                  candidateRediscoverySubscriptionEmail,
                  "CANDIDATE_REDISCOVERY",
                  "EMAIL"
                )}
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl
                id="candidateRediscoverySubscriptionSlack"
                top="25%"
                hidden
              >
                <Checkbox disabled />
              </FormControl>
            </GridItem>
            <GridItem mt="20px" colSpan={2}>
              <FormControl
                id="candidateRediscoverySubscriptionTeams"
                top="25%"
                hidden
              >
                {renderCheckbox(
                  "candidateRediscoverySubscriptionTeams",
                  candidateRediscoverySubscriptionTeams,
                  "CANDIDATE_REDISCOVERY",
                  "TEAMS",
                  { isDisabled: true }
                )}
              </FormControl>
            </GridItem>
          </>
        )}
      </Grid>
    </form>
  );
};

export default NotificationsSettingsForm;
